import { useRef, useState } from 'react'

import { SearchField } from '@/components/ui'
import { useDebouncedCallback } from '@/hooks/useDebouncedCallback'
import { commandModifierLabel, useHotkey } from '@/hooks/useHotkey'

const SEARCH_DEBOUNCE_MS = 300
const SEARCH_HOTKEY = 'K'
const SEARCH_HOTKEY_HINT = `${commandModifierLabel()}+${SEARCH_HOTKEY}`

interface OrgSearchProps {
  /**
   * The term to start from, taken from the URL when the input mounts. The typed
   * text is owned locally from then on, so later changes are not pulled in — the
   * page navigates with `replace`, which means nothing outside this input can
   * change the active term without remounting it.
   */
  initialValue: string
  onChange: (search: string) => void
}

/**
 * Filters the organization directory.
 *
 * The input echoes keystrokes immediately but only reports upward once typing
 * pauses, so the list is not refetched per character. Cmd/Ctrl+K focuses it from
 * anywhere on the page.
 */
export const OrgSearch = ({ initialValue, onChange }: OrgSearchProps) => {
  const [inputValue, setInputValue] = useState(initialValue)
  const inputRef = useRef<HTMLInputElement>(null)
  const reportSearch = useDebouncedCallback(onChange, SEARCH_DEBOUNCE_MS)

  // Selecting rather than only focusing means an existing term is replaced by
  // typing, instead of the caret landing at one end of it.
  useHotkey(SEARCH_HOTKEY, () => {
    // An open dialog or menu traps focus and marks the rest of the app
    // aria-hidden, so the shortcut stands down rather than bouncing focus off
    // the trap and leaving the term selected behind the scrim.
    if (inputRef.current?.closest('[aria-hidden="true"]')) {
      return
    }
    inputRef.current?.focus()
    inputRef.current?.select()
  })

  const handleChange = (nextValue: string) => {
    setInputValue(nextValue)
    // Surrounding whitespace is meaningless to the query but not to the person
    // typing, so trim only what is reported and leave the box as they left it.
    reportSearch(nextValue.trim())
  }

  return (
    <SearchField
      fullWidth
      inputRef={inputRef}
      value={inputValue}
      onChange={(event) => handleChange(event.target.value)}
      placeholder='Search by name or org ID...'
      aria-label='Search organizations'
      hint={SEARCH_HOTKEY_HINT}
    />
  )
}
